import type { CSSProperties, ReactNode } from 'react';

interface ShimmerTextProps {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  durationSeconds?: number;
  /** The text colour the shimmer rests on (the "dark" stops of the
   *  gradient). Pass a CSS color or var. Defaults to body text. */
  baseColor?: string;
  /** The highlight that sweeps across the text. Defaults to a soft
   *  near-white that matches the onboarding scan overlay. */
  highlightColor?: string;
}

/**
 * Text rendered with a sliding gradient that gives it a "live, in
 * progress" feel. Used by ScanOverlay for the onboarding scan messages
 * and by the in-flight memo badge to indicate that an async task is
 * actively making progress.
 *
 * The animation is a CSS keyframe defined here once so callers don't
 * have to repeat the same shimmer block of CSS. Falls back gracefully
 * to plain text in browsers that don't support background-clip.
 */
export default function ShimmerText({
  children,
  className,
  style,
  durationSeconds = 2,
  baseColor = 'var(--color-body-text)',
  highlightColor = 'var(--color-content-light)',
}: ShimmerTextProps) {
  return (
    <>
      <span
        className={className}
        style={{
          background: `linear-gradient(90deg, ${baseColor} 25%, ${highlightColor} 50%, ${baseColor} 75%)`,
          backgroundSize: '200% 100%',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          animation: `shimmer ${durationSeconds}s linear infinite`,
          ...style,
        }}
      >
        {children}
      </span>
      <style>{`
        @keyframes shimmer {
          0% { background-position: 200% center; }
          100% { background-position: -200% center; }
        }
      `}</style>
    </>
  );
}
